import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Descriptions, message } from 'antd';
import { errorMessageWithFieldErrors, space } from '@viq-admin/ui';
import { SalesforceConnectionForm, HubSpotMappingForm, crmSourceLabel } from '@viq-admin/screens';
import type {
  HubSpotPropertyMapWire,
  SalesforceConnectionSaveBody,
} from '@viq-admin/screens';
import { integrationsApi } from '../../api/integrationsApi';

export interface IntegrationEditViewProps {
  id: string;
  /** Called after a successful save — see `IntegrationDetail.tsx` for why. */
  onSaved: () => void;
  /**
   * Forwarded straight through to `SalesforceConnectionForm`'s own
   * `onDirtyChange` — `IntegrationDetail.tsx` wires this to `ConnectionDetail`'s
   * `dirty` prop, restoring the discard-unsaved-changes guard on Cancel (see
   * that file's doc comment).
   */
  onDirtyChange?: (dirty: boolean) => void;
}

/**
 * Edit form for a Salesforce CRM connection, rendered as `ConnectionDetail`'s
 * `editView` slot (see `IntegrationDetail.tsx`). Not a standalone route —
 * `ConnectionDetail` mounts this only while its `editing` state is true and
 * the record's `crmSource` is `'salesforce'` (non-salesforce rows show a
 * "Managed by Connect" note instead; see `ConnectionDetail`'s own doc
 * comment).
 *
 * Fetches its own copy of the record via react-query, keyed IDENTICALLY to
 * `ConnectionDetail`'s internal query (`['integrationsConnection', id]`) so
 * the two share one cache entry: by the time this mounts (Edit is only
 * reachable once the read view has already loaded the record), the fetch
 * resolves from cache with no loading flicker. This mirrors the OLD
 * `IntegrationEdit`'s own separate GET (Refine's `action:'edit'` `useForm`
 * always issued its own `getOne`, distinct from the detail page's `useOne`)
 * — not a new inefficiency introduced here.
 *
 * The company is IMMUTABLE and shown read-only OUTSIDE the injected form
 * (controller ruling 1, "as today") — `companies` is never passed to
 * `SalesforceConnectionForm` here, so no company Select renders; the PUT
 * body still carries the record's own `companyId` (see below), because the
 * backend's PUT schema requires it structurally, not because the operator
 * chose it.
 *
 * `onDirtyChange` is forwarded straight through to `SalesforceConnectionForm`
 * (fix round 1): `IntegrationDetail.tsx` wires it to `ConnectionDetail.dirty`,
 * restoring the discard-unsaved-changes guard on Cancel that the OLD
 * `IntegrationEdit`/`IntegrationDetail` had. `handleSubmit` re-throws on
 * failure specifically so the form's own dirty-reset-on-submit doesn't fire
 * for a failed save (see `SalesforceConnectionForm`'s doc comment).
 */
export function IntegrationEditView({ id, onSaved, onDirtyChange }: IntegrationEditViewProps) {
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);

  const { data: connection } = useQuery({
    queryKey: ['integrationsConnection', id],
    queryFn: () => integrationsApi.getConnection(id),
  });

  async function handleSubmit(body: SalesforceConnectionSaveBody) {
    if (!connection) return;
    setSubmitting(true);
    try {
      await integrationsApi.updateConnection(id, {
        ...body,
        // The backend's PUT schema (CrmConnectionPutInputSchema) requires
        // companyId structurally, same as POST — carry the record's own,
        // already-fetched, immutable value forward. Never sourced from user
        // input: the form above renders no company field on edit.
        companyId: connection.companyId,
      });
      // Refresh the shared cache entry BEFORE flipping back to read mode
      // (IntegrationDetail.tsx's remount-on-save) so the fresh record is
      // already there when the new ConnectionDetail instance mounts.
      await queryClient.invalidateQueries({ queryKey: ['integrationsConnection', id] });
      // Also mark ConnectionsTable's list query stale — a save changes what
      // the list shows (hasSecret, updatedAt), and the console client's 30s
      // staleTime would otherwise serve the old rows on return to the list
      // (the invalidation Refine's useForm used to do).
      await queryClient.invalidateQueries({ queryKey: ['integrationsConnections'] });
      message.success('Connection saved.');
      onSaved();
    } catch (err: unknown) {
      message.error(
        err instanceof Error
          ? err.message
          : ((err as { message?: string })?.message ?? 'Failed to save connection.'),
      );
      // Re-throw so SalesforceConnectionForm's own onSubmit try/catch sees
      // the rejection and skips its onDirtyChange(false) reset — a failed
      // save must NOT clear the discard guard on the operator's still-unsaved
      // edits. The form swallows this itself (see its own doc comment), so
      // it never surfaces as an unhandled rejection here.
      throw err;
    } finally {
      setSubmitting(false);
    }
  }

  // Reachable only transiently: the query key is already warm from
  // ConnectionDetail's own fetch by the time this mounts (see doc comment).
  if (!connection) return null;

  return (
    <>
      <Descriptions column={1} style={{ marginBottom: space.lg }}>
        <Descriptions.Item label="Company">
          {connection.companyName ?? connection.companyId ?? '—'}
        </Descriptions.Item>
        <Descriptions.Item label="CRM Source">
          {crmSourceLabel(connection.crmSource)}
        </Descriptions.Item>
      </Descriptions>

      <SalesforceConnectionForm
        mode="edit"
        initial={connection}
        onSubmit={handleSubmit}
        submitting={submitting}
        onDirtyChange={onDirtyChange}
      />
    </>
  );
}

export interface HubSpotMappingEditViewProps {
  id: string;
  /** Called after a successful save — see `IntegrationDetail.tsx` for why. */
  onSaved: () => void;
  /**
   * Forwarded straight through to `HubSpotMappingForm`'s own `onDirtyChange`
   * — `IntegrationDetail.tsx` wires this to `ConnectionDetail`'s `dirty`
   * prop, same as `IntegrationEditView`'s own `onDirtyChange` above.
   */
  onDirtyChange?: (dirty: boolean) => void;
}

/**
 * Edit form for a HubSpot connection's `propertyMap`, rendered as
 * `ConnectionDetail`'s `hubspotEditView` slot (see `IntegrationDetail.tsx`
 * and that component's own doc comment on the three-way edit gate). Sibling
 * to `IntegrationEditView` above — same shape, same query-key-sharing trick,
 * same re-throw-on-failure dirty-preserving contract — with two differences
 * the HubSpot form needs that the Salesforce one doesn't:
 *
 *  1. A SECOND query, `['integrationsHubSpotProps', id]`, loads the portal's
 *     writable contact properties (`api.listHubSpotProperties`) to power
 *     `HubSpotMappingForm`'s pickers. On failure it degrades to `null`
 *     (never re-thrown, never blocks rendering) — `HubSpotMappingForm`'s own
 *     picker-degrade path (free-text inputs + a warning) takes over, per the
 *     "form must never be bricked by the proxy failing" rule (spec §5.2).
 *     This component still waits for that FIRST fetch to settle (loading or
 *     not) before rendering the form at all, so the pickers don't render
 *     once as `null` (free text) and then flash into `Select`s a moment
 *     later — an in-flight refetch on an already-warm cache entry (e.g. a
 *     second Edit click) does NOT re-block, since `isLoading` is only true
 *     on a query's first fetch with no data yet.
 *  2. `handleSubmit` attaches the record's own (already-fetched, immutable)
 *     `companyId` the same way `IntegrationEditView.handleSubmit` does for
 *     `updateConnection` — required because the backend's PUT schema
 *     (`HubSpotMappingPutSchema`) requires `companyId` structurally, AND
 *     `HubSpotMappingSaveBody.companyId` is now a required string on the
 *     wire type too (fix round 2 — Task 7 had left it optional, so a miss
 *     surfaced as a runtime 422 instead of a compile error). `connection`
 *     itself is still typed `CrmConnectionRow`, where `companyId` STAYS
 *     optional (the shared type across both planes — the portal's
 *     own-company plane omits it) — so `handleSubmit` guards and bails
 *     loudly rather than asserting a value that's only structurally
 *     guaranteed on the console's own rows.
 *     Its success toast nudges toward Test Connection (spec §5.2) instead of
 *     the Salesforce form's generic "Connection saved." — a mapping save
 *     doesn't itself prove the properties still exist on the portal.
 *
 * Both `listHubSpotProperties`/`updateHubSpotMapping` are asserted non-null
 * below: the shared `IntegrationsApi` interface types them optional only
 * because the portal doesn't implement them (Task 7) — the console's own
 * `integrationsApi.ts` always does, and this component is reachable only
 * from a hubspot row's console edit slot (same idiom as LeadDetailBody's
 * `api.updateDiscoveryAnswer!`, packages/screens/src/leadDetail/LeadDetailBody.tsx).
 */
export function HubSpotMappingEditView({ id, onSaved, onDirtyChange }: HubSpotMappingEditViewProps) {
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);

  const { data: connection } = useQuery({
    queryKey: ['integrationsConnection', id],
    queryFn: () => integrationsApi.getConnection(id),
  });

  const propertiesQuery = useQuery({
    queryKey: ['integrationsHubSpotProps', id],
    queryFn: () => integrationsApi.listHubSpotProperties!(id),
  });

  async function handleSubmit(map: HubSpotPropertyMapWire) {
    if (!connection) return;
    // `CrmConnectionRow.companyId` stays optional on the shared type (the
    // portal's own-company plane omits it) even though `HubSpotMappingSaveBody`
    // below now requires a `string` (fix round 2, Finding 3) — this component
    // is reachable only from the console's cross-tenant list, where the
    // superadmin API always returns a companyId, so this should never
    // trip. Guard and bail loudly rather than asserting: a silently
    // `undefined` companyId sent as a JSON body would still round-trip
    // through `JSON.stringify` (dropped, not `"undefined"`) and hit the same
    // "structurally required" 422 Finding 3 was about, just with the type
    // system no longer able to catch the miss at the call site above.
    if (!connection.companyId) {
      message.error('Cannot save — this connection is missing its company id.');
      return;
    }
    const companyId = connection.companyId;
    setSubmitting(true);
    try {
      await integrationsApi.updateHubSpotMapping!(id, {
        crmSource: 'hubspot',
        // The backend's PUT schema requires companyId structurally, same as
        // updateConnection above — carry the record's own, already-fetched,
        // immutable value forward. Never sourced from user input:
        // HubSpotMappingForm renders no company field.
        companyId,
        config: { propertyMap: map },
      });
      // Refresh the shared cache entry BEFORE flipping back to read mode
      // (IntegrationDetail.tsx's remount-on-save) so the fresh record is
      // already there when the new ConnectionDetail instance mounts.
      await queryClient.invalidateQueries({ queryKey: ['integrationsConnection', id] });
      // Also mark ConnectionsTable's list query stale, same reason
      // IntegrationEditView's handleSubmit does.
      await queryClient.invalidateQueries({ queryKey: ['integrationsConnections'] });
      message.success('Mapping saved — run Test Connection to validate it against the portal');
      onSaved();
    } catch (err: unknown) {
      // Finding 2 (final review): a 422 here carries the actionable detail
      // (reserved name, caps, untrimmed, >1000 value) in the backend's
      // `details.fieldErrors`, which `fetchJson` (apps/console/src/api/dataProvider.ts)
      // already exposes on `HttpError.errors` — the plain `err.message` alone
      // is just "Validation failed", which names no field and no rule.
      // `errorMessageWithFieldErrors` (@viq-admin/ui) appends every field's
      // issue messages; this is the SAME helper the portal's own
      // IntegrationsPage.tsx already uses for its Salesforce save toast, so
      // this stays consistent with that sibling flow rather than
      // reimplementing a narrower one-key accessor here.
      message.error(errorMessageWithFieldErrors(err, 'Failed to save mapping.'));
      // Re-throw so HubSpotMappingForm's own onSubmit try/catch sees the
      // rejection and skips its onDirtyChange(false) reset — see that
      // component's doc comment.
      throw err;
    } finally {
      setSubmitting(false);
    }
  }

  // Waits for the record (warm from ConnectionDetail's own fetch, see
  // IntegrationEditView's doc comment) AND the properties' first fetch, so
  // the form mounts once, already knowing whether pickers are available —
  // see doc comment point 1 above.
  if (!connection || propertiesQuery.isLoading) return null;
  const properties = propertiesQuery.isError ? null : (propertiesQuery.data ?? null);

  return (
    <>
      <Descriptions column={1} style={{ marginBottom: space.lg }}>
        <Descriptions.Item label="Company">
          {connection.companyName ?? connection.companyId ?? '—'}
        </Descriptions.Item>
        <Descriptions.Item label="CRM Source">
          {crmSourceLabel(connection.crmSource)}
        </Descriptions.Item>
      </Descriptions>

      <HubSpotMappingForm
        initial={(connection.config as { propertyMap?: HubSpotPropertyMapWire }).propertyMap}
        properties={properties}
        onSubmit={handleSubmit}
        submitting={submitting}
        onDirtyChange={onDirtyChange}
      />
    </>
  );
}
